(() => {
  const initLiveSearch = () => {
    const search = document.querySelector("header .customSearch");
    if (!search || search.dataset.liveSearchReady === "true") return;

    const input = search.querySelector("[data-live-search-input], .Search__Input");
    const results = search.querySelector(".Search__Results");
    const resultsHost =
      results?.querySelector("[data-live-search-results], .results") || results;
    const closeButton = search.querySelector(".closeCustomSearch");
    const clearIcon = search.querySelector(".header__search-icon");
    const pageOverlay = document.querySelector(".PageOverlay");
    const searchTriggers = Array.from(document.querySelectorAll(".search__uniq"));
    let searchStatus = search.querySelector("#HeaderSearchStatus");

    if (!input || !results || !resultsHost) return;
    search.dataset.liveSearchReady = "true";

    if (!search.id) search.id = "Search";
    if (!results.id) results.id = "HeaderSearchResults";
    if (!input.id) input.id = "HeaderSearchInput";
    if (!searchStatus) {
      searchStatus = document.createElement("span");
      searchStatus.id = "HeaderSearchStatus";
      searchStatus.className = "u-visually-hidden";
      searchStatus.setAttribute("role", "status");
      searchStatus.setAttribute("aria-live", "polite");
      searchStatus.setAttribute("aria-atomic", "true");
      input.insertAdjacentElement("afterend", searchStatus);
    }
    input.setAttribute("aria-controls", results.id);
    input.setAttribute("aria-describedby", searchStatus.id);
    results.setAttribute("aria-busy", "false");
    results.removeAttribute("aria-live");
    searchTriggers.forEach((trigger) => {
      trigger.setAttribute("aria-controls", search.id);
      trigger.setAttribute("aria-haspopup", "dialog");
      trigger.setAttribute("aria-expanded", "false");
    });
    if (results.getAttribute("aria-hidden") !== "false") {
      resultsHost.innerHTML = "";
    }

    const mobileMedia = window.matchMedia("(max-width: 1139px)");
    const searchMode = (window.theme && window.theme.searchMode) || "product";
    const searchBase =
      (window.routes && window.routes.searchUrl) || "/search";
    const secondaryTypes = searchMode
      .split(",")
      .map((type) => type.trim())
      .filter((type) => type && type !== "product")
      .join(",");
    const requestTypes = secondaryTypes
      ? ["product", secondaryTypes]
      : ["product"];
    const cache = new Map();

    let desiredTerm = "";
    let scheduledTerm = "";
    let activeTerm = "";
    let renderedTerm = "";
    let debounceTimer = null;
    let closeTimer = null;
    let requestId = 0;
    let activeRequest = null;
    let themeIsLoading = false;
    let scrollLocked = false;
    let lockedPageX = 0;
    let lockedPageY = 0;
    let composing = false;
    let lastOpener = null;
    let announcementFrame = null;

    const normalizeTerm = (raw) =>
      String(raw).trim().split(/\s+/).filter(Boolean).join(" ");

    const announceSearch = (message) => {
      window.cancelAnimationFrame(announcementFrame);
      searchStatus.textContent = "";
      announcementFrame = window.requestAnimationFrame(() => {
        searchStatus.textContent = message;
      });
    };

    const setClearControl = (raw) => {
      if (!clearIcon) return;
      const hasValue = raw !== "";
      clearIcon.classList.toggle("has-value", hasValue);
      clearIcon.hidden = false;
      clearIcon.setAttribute("aria-hidden", hasValue ? "false" : "true");

      if (clearIcon.tagName === "BUTTON") {
        clearIcon.disabled = !hasValue;
      } else {
        clearIcon.setAttribute("role", "button");
        clearIcon.setAttribute("aria-label", clearIcon.getAttribute("aria-label") || "Clear search");
        clearIcon.tabIndex = hasValue ? 0 : -1;
      }
    };

    const wildcardTerm = (term) => {
      const tokens = term.split(" ");
      if (!/\d/.test(tokens[tokens.length - 1])) tokens[tokens.length - 1] += "*";
      return tokens.join(" ");
    };

    const twoColumn = (primary, secondary) =>
      '<div class="PageLayout PageLayout--breakLap">' +
      '<div class="PageLayout__Section">' +
      primary +
      "</div>" +
      '<div class="PageLayout__Section PageLayout__Section--secondary">' +
      secondary +
      "</div>" +
      "</div>";

    const dispatchThemeLoading = (on) => {
      if (themeIsLoading === on) return;
      themeIsLoading = on;
      document.dispatchEvent(
        new CustomEvent(on ? "theme:loading:start" : "theme:loading:end"),
      );
    };

    const showResults = () => {
      results.setAttribute("aria-hidden", "false");
      document.body.classList.add("is-search-panel");
    };

    const setLoading = (on) => {
      results.setAttribute("aria-busy", on ? "true" : "false");
      document.body.classList.toggle(
        "is-search-loading",
        on && renderedTerm === "",
      );
      dispatchThemeLoading(on);
    };

    const holdOverlayThroughFade = () => {
      document.body.classList.add("is-search-closing");
      clearTimeout(closeTimer);
      closeTimer = setTimeout(
        () => document.body.classList.remove("is-search-closing"),
        350,
      );
    };

    const setSearching = (on) => {
      if (on) {
        clearTimeout(closeTimer);
        document.body.classList.remove("is-search-closing");
      } else {
        if (document.body.classList.contains("is-searching")) {
          holdOverlayThroughFade();
        }
        document.body.classList.remove("is-search-panel");
      }
      document.body.classList.toggle("is-searching", on);
    };

    const blockBackgroundScroll = (event) => {
      if (!results.contains(event.target)) event.preventDefault();
    };

    const holdPagePosition = () => {
      if (
        !scrollLocked ||
        document.activeElement === input ||
        (window.scrollX === lockedPageX && window.scrollY === lockedPageY)
      ) {
        return;
      }
      window.scrollTo(lockedPageX, lockedPageY);
    };

    const lockPageScroll = () => {
      if (scrollLocked) return;
      lockedPageX = window.scrollX;
      lockedPageY = window.scrollY;
      scrollLocked = true;
      window.addEventListener("scroll", holdPagePosition, { passive: true });
      window.addEventListener("wheel", blockBackgroundScroll, {
        passive: false,
      });
      window.addEventListener("touchmove", blockBackgroundScroll, {
        passive: false,
      });
    };

    const unlockPageScroll = () => {
      if (!scrollLocked) return;
      scrollLocked = false;
      window.removeEventListener("scroll", holdPagePosition);
      window.removeEventListener("wheel", blockBackgroundScroll);
      window.removeEventListener("touchmove", blockBackgroundScroll);
    };

    const cancelWork = () => {
      clearTimeout(debounceTimer);
      debounceTimer = null;
      scheduledTerm = "";
      requestId += 1;

      const request = activeRequest;
      activeRequest = null;
      activeTerm = "";
      request?.xhrs.forEach((xhr) => {
        if (xhr.readyState !== XMLHttpRequest.DONE) xhr.abort();
      });
    };

    const clearResults = () => {
      cancelWork();
      desiredTerm = "";
      renderedTerm = "";
      resultsHost.innerHTML = "";
      results.setAttribute("aria-hidden", "true");
      window.cancelAnimationFrame(announcementFrame);
      announcementFrame = null;
      searchStatus.textContent = "";
      setLoading(false);
      setSearching(false);
      unlockPageScroll();
    };

    const cacheResults = (term, contents) => {
      cache.delete(term);
      cache.set(term, contents);
      if (cache.size > 20) cache.delete(cache.keys().next().value);
    };

    const renderResults = (term, contents) => {
      renderedTerm = term;
      resultsHost.innerHTML = secondaryTypes
        ? twoColumn(contents[0], contents[1])
        : contents[0];
      showResults();
      results.scrollTop = 0;
      setLoading(false);
      announceSearch(
        searchStatus.dataset.resultsMessage || "Search results updated.",
      );
    };

    const renderError = () => {
      renderedTerm = "";
      resultsHost.innerHTML =
        '<div class="Segment custom__segment live-search__error">' +
        '<div class="Segment__Title Segment__Title--flexed flexed-c">' +
        '<span class="search__results-title">Search is temporarily unavailable.</span>' +
        "</div>" +
        '<div class="Segment__Content custom__segment">' +
        '<p class="empty-state">Try again or press Enter to view all search results.</p>' +
        '<button class="Link Link--secondary" type="button" data-live-search-retry>Try again</button>' +
        "</div>" +
        "</div>";
      showResults();
      setLoading(false);
      announceSearch(
        searchStatus.dataset.errorMessage ||
          "Search results could not be loaded.",
      );
    };

    const searchUrl = (type, term) => {
      const url = new URL(searchBase, window.location.origin);
      url.searchParams.set("view", "ajax");
      url.searchParams.set("q", wildcardTerm(term));
      url.searchParams.set("type", type);
      url.searchParams.set("options[prefix]", "last");
      return url.toString();
    };

    const requestHtml = (request, type) =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        request.xhrs.push(xhr);
        xhr.open("GET", searchUrl(type, request.term), true);
        xhr.timeout = 8000;
        xhr.setRequestHeader("Accept", "text/html");
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.responseText);
          } else {
            reject(new Error(`Search request failed with ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error("Search request failed"));
        xhr.ontimeout = () => reject(new Error("Search request timed out"));
        xhr.onabort = () => reject(new Error("Search request cancelled"));
        xhr.send();
      });

    const ownsRequest = (request) =>
      activeRequest === request &&
      request.id === requestId &&
      request.term === desiredTerm;

    const startRequest = (term, id) => {
      if (id !== requestId || term !== desiredTerm) return;

      const cached = cache.get(term);
      if (cached) {
        renderResults(term, cached);
        return;
      }

      const request = { id, term, xhrs: [] };
      activeRequest = request;
      activeTerm = term;

      Promise.all(requestTypes.map((type) => requestHtml(request, type))).then(
        (contents) => {
          if (!ownsRequest(request)) return;
          activeRequest = null;
          activeTerm = "";
          cacheResults(term, contents);
          renderResults(term, contents);
        },
        () => {
          if (!ownsRequest(request)) return;
          activeRequest = null;
          activeTerm = "";
          request.xhrs.forEach((xhr) => {
            if (xhr.readyState !== XMLHttpRequest.DONE) xhr.abort();
          });
          renderError();
        },
      );
    };

    const queueSearch = (term, force = false) => {
      const termIsOwned =
        scheduledTerm === term ||
        activeTerm === term ||
        renderedTerm === term;
      if (!force && term === desiredTerm && termIsOwned) {
        showResults();
        return;
      }

      cancelWork();
      desiredTerm = term;
      showResults();
      searchStatus.textContent = "";
      setLoading(true);

      const cached = cache.get(term);
      if (cached) {
        renderResults(term, cached);
        return;
      }

      const id = requestId;
      scheduledTerm = term;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        if (
          id !== requestId ||
          scheduledTerm !== term ||
          desiredTerm !== term
        ) {
          return;
        }
        scheduledTerm = "";
        startRequest(term, id);
      }, 300);
    };

    const handleInput = () => {
      if (mobileMedia.matches && !search.classList.contains("is-show")) {
        input.value = "";
        setClearControl("");
        clearResults();
        return;
      }

      const raw = input.value;
      const term = normalizeTerm(raw);
      setClearControl(raw);

      if (!term) {
        clearResults();
        return;
      }

      setSearching(true);
      lockPageScroll();
      queueSearch(term);
    };

    const closeSearch = (restoreFocus = false) => {
      search.classList.remove("is-show");
      search.setAttribute(
        "aria-hidden",
        mobileMedia.matches ? "true" : "false",
      );
      searchTriggers.forEach((trigger) =>
        trigger.setAttribute("aria-expanded", "false"),
      );
      composing = false;
      input.blur();
      input.value = "";
      setClearControl("");
      clearResults();

      if (restoreFocus) {
        window.requestAnimationFrame(() => {
          if (
            mobileMedia.matches &&
            lastOpener &&
            document.contains(lastOpener)
          ) {
            lastOpener.focus({ preventScroll: true });
          } else if (!mobileMedia.matches) {
            input.focus({ preventScroll: true });
          }
        });
      }
    };

    input.addEventListener("compositionstart", () => {
      composing = true;
      cancelWork();
      setLoading(false);
    });

    input.addEventListener(
      "input",
      (event) => {
        event.stopImmediatePropagation();
        if (composing || event.isComposing) {
          setClearControl(input.value);
          return;
        }
        handleInput();
      },
      true,
    );

    input.addEventListener("compositionend", () => {
      composing = false;
      setTimeout(handleInput, 0);
    });

    input.addEventListener("blur", () => {
      composing = false;
    });

    input.addEventListener("search", handleInput);

    input.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Enter") return;
        event.stopImmediatePropagation();
        if (event.isComposing || composing || event.keyCode === 229) return;
        cancelWork();
        setLoading(false);
      },
      true,
    );

    clearIcon?.addEventListener("click", () => {
      input.value = "";
      handleInput();
      input.focus({ preventScroll: true });
    });
    clearIcon?.addEventListener("keydown", (event) => {
      if (
        clearIcon.tagName === "BUTTON" ||
        (event.key !== "Enter" && event.key !== " ")
      ) {
        return;
      }
      event.preventDefault();
      clearIcon.click();
    });

    results.addEventListener("click", (event) => {
      if (!event.target.closest("[data-live-search-retry]")) return;
      const term = normalizeTerm(input.value);
      if (term) queueSearch(term, true);
    });

    searchTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        lastOpener = trigger;
        search.classList.add("is-show");
        search.inert = false;
        search.removeAttribute("inert");
        search.setAttribute("aria-hidden", "false");
        searchTriggers.forEach((searchTrigger) =>
          searchTrigger.setAttribute("aria-expanded", "true"),
        );
        input.focus({ preventScroll: true });
      });
    });

    closeButton?.addEventListener("click", () => closeSearch(true));

    pageOverlay?.addEventListener("click", () => {
      if (
        results.getAttribute("aria-hidden") === "false" ||
        search.classList.contains("is-show")
      ) {
        closeSearch(false);
      }
    });

    document.addEventListener("keydown", (event) => {
      if (
        event.key === "Escape" &&
        (results.getAttribute("aria-hidden") === "false" ||
          search.classList.contains("is-show"))
      ) {
        event.preventDefault();
        closeSearch(true);
      }
    });

    const syncVisibility = () => {
      if (mobileMedia.matches) {
        search.setAttribute("role", "dialog");
        search.setAttribute("aria-modal", "true");
        searchTriggers.forEach((trigger) =>
          trigger.setAttribute("aria-haspopup", "dialog"),
        );
        if (!search.classList.contains("is-show")) {
          search.setAttribute("aria-hidden", "true");
          searchTriggers.forEach((trigger) =>
            trigger.setAttribute("aria-expanded", "false"),
          );
          if (desiredTerm) closeSearch();
        }
      } else {
        search.removeAttribute("role");
        search.removeAttribute("aria-modal");
        searchTriggers.forEach((trigger) =>
          trigger.removeAttribute("aria-haspopup"),
        );
        search.inert = false;
        search.removeAttribute("inert");
        search.classList.remove("is-show");
        search.setAttribute("aria-hidden", "false");
        searchTriggers.forEach((trigger) =>
          trigger.setAttribute("aria-expanded", "false"),
        );
      }
    };

    setClearControl(input.value);
    syncVisibility();

    if (document.activeElement === input && input.value.trim()) handleInput();
    if (mobileMedia.addEventListener) {
      mobileMedia.addEventListener("change", syncVisibility);
    } else {
      mobileMedia.addListener(syncVisibility);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initLiveSearch, {
      once: true,
    });
  } else {
    initLiveSearch();
  }
})();

window.removeURLParameter = function (parameter) {
  var url = window.location.href;
  var urlparts = url.split("?");
  if (urlparts.length >= 2) {
    var prefix = encodeURIComponent(parameter) + "=";
    var pars = urlparts[1].split(/[&;]/g);

    for (var i = pars.length; i-- > 0; ) {
      if (pars[i].lastIndexOf(prefix, 0) !== -1) {
        pars.splice(i, 1);
      }
    }

    url = urlparts[0] + "?" + pars.join("&");
    return url;
  } else {
    return url;
  }
};
let sticky = document.querySelector("sticky-atc");

const discontinuedMap = () => {
  const el = document.querySelector("[data-variants-discontinued-json]");
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (err) {
    return null;
  }
};

const applyDiscontinued = (isDiscontinued) => {
  const docData = document.querySelectorAll(
    ".affirm-as-low-as, .pdp-atc-divider, .metafield-data-additional, .product--button-wrapper, .ProductMeta__ShareButtons, .product-info-tabination, .ProductMeta__PriceList, .offer_line, .pdp-call-btn, .pdp-shipping-surcharge",
  );
  const discontinuedEle = document.querySelector(".ProductMeta__discontinued");
  const stickyBar = document.querySelector("sticky-atc");
  docData.forEach((ele) => ele.classList.toggle("d-none", isDiscontinued));
  if (
    discontinuedEle &&
    !discontinuedEle.classList.contains("is_product_discontinued")
  ) {
    discontinuedEle.classList.toggle("d-none", !isDiscontinued);
  }
  if (stickyBar) {
    stickyBar.style.visibility = isDiscontinued ? "hidden" : "unset";
  }
};

{
  const map = discontinuedMap();
  const selected = document.querySelector('[name="id"]');
  const entry = map && selected && map[`${selected.value}`];
  if (sticky && entry && entry.discontinued) {
    sticky.style.visibility = "hidden";
  }
}

document.addEventListener("variant:changed", (e) => {
  const variant = e.detail && e.detail.variant;
  const map = discontinuedMap();
  if (!variant || !map) return;
  const entry = map[`${variant.id}`];
  if (!entry) return;
  applyDiscontinued(!!entry.discontinued);
});

(() => {
  const selector = ".swiper--thumbnails";
  const desktop = window.matchMedia("(min-width: 1140px)");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const controllers = new Map();
  const resources = new Map();

  const loadResource = (url, isStyle) => {
    if (!url) return Promise.reject(new Error("Missing thumbnail asset URL"));
    if (!isStyle && typeof window.Swiper === "function") return Promise.resolve();
    const assetUrl = new URL(url, document.baseURI).href;
    if (resources.has(assetUrl)) return resources.get(assetUrl);

    const promise = new Promise((resolve, reject) => {
      const elements = document.querySelectorAll(isStyle ? 'link[rel="stylesheet"]' : "script[src]");
      let element = Array.from(elements).find((node) => (isStyle ? node.href : node.src) === assetUrl);
      if (element && isStyle && element.sheet) {
        resolve();
        return;
      }

      const isNew = !element;
      if (!element) {
        element = document.createElement(isStyle ? "link" : "script");
        if (isStyle) {
          element.rel = "stylesheet";
          element.href = assetUrl;
        } else {
          element.src = assetUrl;
          element.async = true;
        }
      }
      const cleanup = () => {
        element.removeEventListener("load", onLoad);
        element.removeEventListener("error", onError);
      };
      const onLoad = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        if (isNew) element.remove();
        resources.delete(assetUrl);
        reject(new Error("Thumbnail asset failed to load"));
      };
      element.addEventListener("load", onLoad);
      element.addEventListener("error", onError);
      if (isNew) document.head.appendChild(element);
    });
    resources.set(assetUrl, promise);
    return promise;
  };

  const selectedIndex = (rail) => {
    const gallery = rail.closest(".Product__Gallery");
    const selected = gallery && gallery.querySelector(".Product__SlideItem.is-selected");
    const mediaId = selected && selected.getAttribute("data-media-id");
    const slides = Array.from(rail.querySelectorAll(".swiper-slide"));
    const index = slides.findIndex((slide) => {
      const link = slide.querySelector(".Product__SlideshowNavImage");
      return link && (mediaId ? link.getAttribute("data-media-id") === mediaId : link.classList.contains("is-selected"));
    });
    return Math.max(0, index);
  };

  const destroy = (controller) => {
    if (controller.swiper && !controller.swiper.destroyed) {
      controller.swiper.destroy(true, true);
    }
    controller.swiper = null;
  };

  const update = (rail, controller) => {
    if (!desktop.matches || !rail.isConnected) {
      destroy(controller);
      return;
    }
    if (controller.swiper || controller.loading || window.getComputedStyle(rail).display === "none") return;

    controller.loading = Promise.all([
      loadResource(rail.dataset.swiperScript, false),
      loadResource(rail.dataset.swiperStyles, true),
    ]).then(() => {
      if (!desktop.matches || !rail.isConnected || !controllers.has(rail)) return;
      if (window.getComputedStyle(rail).display === "none") return;
      rail.classList.remove("swiper-unavailable");
      controller.swiper = new window.Swiper(rail, {
        direction: "vertical",
        slidesPerView: 4,
        spaceBetween: 0,
        autoHeight: true,
        loop: false,
        grabCursor: true,
        initialSlide: selectedIndex(rail),
        speed: reducedMotion.matches ? 0 : 800,
        navigation: {
          nextEl: rail.querySelector(".swiper-button-next"),
          prevEl: rail.querySelector(".swiper-button-prev"),
        },
      });
    }).catch(() => {
      rail.classList.add("swiper-unavailable");
    }).finally(() => {
      controller.loading = null;
    });
  };

  const discover = (root) => {
    const rails = Array.from(root.querySelectorAll(selector));
    if (root.matches && root.matches(selector)) rails.unshift(root);
    rails.forEach((rail) => {
      if (!controllers.has(rail)) controllers.set(rail, { swiper: null, loading: null });
      update(rail, controllers.get(rail));
    });
  };

  const listen = (query, callback) => {
    if (query.addEventListener) query.addEventListener("change", callback);
    else query.addListener(callback);
  };
  listen(desktop, () => {
    controllers.forEach((controller, rail) => {
      update(rail, controller);
      if (!rail.isConnected) controllers.delete(rail);
    });
  });
  listen(reducedMotion, () => {
    controllers.forEach((controller) => {
      if (controller.swiper) controller.swiper.params.speed = reducedMotion.matches ? 0 : 800;
    });
  });
  document.addEventListener("shopify:section:load", (event) => discover(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    controllers.forEach((controller, rail) => {
      if (event.target.contains(rail)) {
        destroy(controller);
        controllers.delete(rail);
      }
    });
  });
  discover(document);
})();

window.addEventListener("DOMContentLoaded", () => {
  const sendUsYourCartModel = document.querySelector("#share-cart-modal form");
  sendUsYourCartModel?.addEventListener("submit", (e) => {
    var data = window.data.cart || {};
    var cart = {
      total_price: data?.total_price / 100,
      $value: data?.total_price / 100,
      total_discount: data?.total_discount,
      original_total_price: data?.original_total_price / 100,
      items: data?.items,
    };
    if (typeof item !== "undefined") {
      cart = Object.assign(cart, item);
    }
    if (typeof _learnq !== "undefined") {
      _learnq.push(["track", "Added to Cart", cart]);
    }
  });
});

{
  const releaseBuyButtons = () => {
    const buyBtnWrapper = document.querySelector(".product--button-wrapper");
    if (buyBtnWrapper) {
      buyBtnWrapper.style.pointerEvents = null;
    }
  };

  const hulkOptions = document.querySelector('script[src*="product-options-by-hulkapps"]');
  if (hulkOptions) {
    hulkOptions.addEventListener("load", releaseBuyButtons, { once: true });
    hulkOptions.addEventListener("error", releaseBuyButtons, { once: true });
  } else {
    releaseBuyButtons();
  }

  ["DOMContentLoaded", "resize"].forEach((e) => {
    window.addEventListener(e, releaseBuyButtons);
  });
}

document.addEventListener(
  "click",
  function (e) {
    var btn =
      e.target.closest &&
      e.target.closest('[data-action="toggle-collapsible"]');
    if (!btn) return;
    var collapsible = btn.closest(".Collapsible");
    var inner = collapsible && collapsible.querySelector(".Collapsible__Inner");
    if (!inner) return;

    if (collapsible.dataset.toggling === "1") {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    collapsible.dataset.toggling = "1";
    var clear = function () {
      collapsible.dataset.toggling = "";
      inner.removeEventListener("transitionend", onEnd);
      clearTimeout(fallback);
    };
    var onEnd = function (ev) {
      if (ev.target === inner && ev.propertyName === "height") clear();
    };
    inner.addEventListener("transitionend", onEnd);
    var fallback = setTimeout(clear, 500);
  },
  true,
);

(function () {
  var BADGE_INSET = 6;
  var LETTERBOXED = window.matchMedia("(max-width: 640px)");

  var badge = document.querySelector("[data-pdp-online-exclusive-badge]");
  var slideshow = document.querySelector("[data-pdp-slideshow]");
  if (!badge || !slideshow) return;

  function clearPlacement() {
    badge.style.removeProperty("top");
    badge.style.removeProperty("left");
  }

  function slideRatio(slide) {
    var frame = slide.querySelector(".AspectRatio");
    var ratio = frame
      ? parseFloat(frame.style.getPropertyValue("--aspect-ratio"))
      : NaN;
    if (ratio > 0) return ratio;

    var img = slide.querySelector("img");
    if (!img) return NaN;
    var width = parseFloat(img.getAttribute("data-max-width")) || img.naturalWidth;
    var height =
      parseFloat(img.getAttribute("data-max-height")) || img.naturalHeight;
    return width > 0 && height > 0 ? width / height : NaN;
  }

  function placeBadge() {
    if (!LETTERBOXED.matches) {
      clearPlacement();
      return;
    }

    var stage =
      slideshow.querySelector(".flickity-viewport") ||
      slideshow.querySelector(".Product__SlideItem.is-selected") ||
      slideshow.querySelector(".Product__SlideItem");
    if (!stage) return;

    var stageBox = stage.getBoundingClientRect();
    var frameBox = slideshow.getBoundingClientRect();
    if (!stageBox.width || !stageBox.height) return;

    var slides = slideshow.querySelectorAll(
      '.Product__SlideItem[data-media-type="image"]',
    );
    var insetX = -1;
    var insetY = -1;
    for (var i = 0; i < slides.length; i++) {
      var ratio = slideRatio(slides[i]);
      if (!(ratio > 0)) continue;
      var paintedW = Math.min(stageBox.width, stageBox.height * ratio);
      var paintedH = Math.min(stageBox.height, stageBox.width / ratio);
      insetX = Math.max(insetX, (stageBox.width - paintedW) / 2);
      insetY = Math.max(insetY, (stageBox.height - paintedH) / 2);
    }

    if (insetX < 0) {
      clearPlacement();
      return;
    }

    badge.style.left =
      Math.round(stageBox.left - frameBox.left + insetX + BADGE_INSET) + "px";
    badge.style.top =
      Math.round(stageBox.top - frameBox.top + insetY + BADGE_INSET) + "px";
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      placeBadge();
    });
  }

  schedule();
  window.addEventListener("resize", schedule);
  window.addEventListener("orientationchange", schedule);
  if (window.ResizeObserver) new ResizeObserver(schedule).observe(slideshow);
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(slideshow, { childList: true });
  }
})();

(function () {
  var SIGNUP_LINK = 'a[href$="#attentive-signup"]';
  var DESKTOP = window.matchMedia("(min-width: 760px)");
  var DESKTOP_CREATIVE = 1466999;
  var MOBILE_CREATIVE = 1467089;
  var waiting = false;

  document.querySelectorAll(SIGNUP_LINK).forEach(function (link) {
    link.setAttribute("aria-haspopup", "dialog");
  });

  document.addEventListener(
    "click",
    function (e) {
      var link = e.target.closest && e.target.closest(SIGNUP_LINK);
      if (!link) return;
      e.preventDefault();
      if (waiting) return;
      waiting = true;
      var creative = DESKTOP.matches ? DESKTOP_CREATIVE : MOBILE_CREATIVE;
      var tries = 0;
      (function open() {
        var attentive = window.__attentive;
        if (attentive && typeof attentive.trigger === "function") {
          waiting = false;
          attentive.trigger(null, null, null, creative);
        } else if (++tries < 75) {
          setTimeout(open, 200);
        } else {
          waiting = false;
        }
      })();
    },
    true,
  );
})();
